import { useEffect, useState } from "react";
import { FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Loading } from "../../components/shared";
import { formatDuration, formatTimestamp } from "./ExpertSessions";

const TYPES = {
  appointments: "Appointment History",
  consultations: "Consultation History",
  commercial: "Commercial Calculation History",
};

const formatPaise = (paise) => {
  const value = Number(paise || 0) / 100;
  return `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function ExpertReports({
  initialType = "appointments",
  locked = false,
}) {
  const [type, setType] = useState(initialType);
  const [options, setOptions] = useState(null);
  const [result, setResult] = useState(null);
  const [filters, setFilters] = useState({
    from_date: "",
    to_date: "",
    service_id: "",
    status: "",
    mode: "",
  });

  const load = async (nextPage = 1) => {
    setResult(null);
    try {
      const response = await api.get(`/expert-portal/reports/history/${type}`, {
        params: { ...filters, page: nextPage },
      });
      setResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load this report");
      setResult({
        rows: [],
        pagination: { page: 1, total: 0, has_next: false },
      });
    }
  };

  useEffect(() => {
    api
      .get("/expert-portal/reports/options")
      .then((response) => setOptions(response.data))
      .catch(() => setOptions({ services: [] }));
  }, []);

  useEffect(() => {
    load(1);
  }, [type]);

  const selectType = (nextType) => {
    setFilters({
      from_date: "",
      to_date: "",
      service_id: "",
      status: "",
      mode: "",
    });
    setType(nextType);
  };

  const download = async () => {
    try {
      const response = await api.get(`/expert-portal/reports/${type}.csv`, {
        params: filters,
        responseType: "blob",
      });
      const url = URL.createObjectURL(
        new Blob([response.data], { type: "text/csv" }),
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `expert-${type}-history.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not export this report",
      );
    }
  };

  if (!options || !result) return <Loading />;
  const statuses =
    options[
      `${type === "commercial" ? "commercial" : type.slice(0, -1)}_statuses`
    ] || [];

  return (
    <section data-testid="expert-reports-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">
            {locked ? TYPES[type] : "Reports"}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Filtered history is scoped to your Expert account.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="expert-reports-refresh"
            onClick={() => load(1)}
            size="sm"
            type="button"
            variant="outline"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button
            data-testid="expert-reports-export"
            onClick={download}
            size="sm"
            type="button"
          >
            <FileDown className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {!locked && (
        <div
          className="mt-6 flex flex-wrap gap-2"
          data-testid="expert-reports-tabs"
        >
          {Object.entries(TYPES).map(([key, name]) => (
            <button
              className={`border px-3 py-2 text-sm font-semibold ${type === key ? "border-blue-900 bg-blue-50 text-blue-900" : "border-slate-200 text-slate-600"}`}
              data-testid={`expert-report-type-${key}`}
              key={key}
              onClick={() => selectType(key)}
              type="button"
            >
              {name}
            </button>
          ))}
        </div>
      )}

      <div
        className="mt-6 grid gap-3 border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-5"
        data-testid="expert-report-filters"
      >
        <Filter label="From Date">
          <input
            data-testid="expert-report-from-date"
            onChange={(event) =>
              setFilters({ ...filters, from_date: event.target.value })
            }
            type="date"
            value={filters.from_date}
          />
        </Filter>
        <Filter label="To Date">
          <input
            data-testid="expert-report-to-date"
            onChange={(event) =>
              setFilters({ ...filters, to_date: event.target.value })
            }
            type="date"
            value={filters.to_date}
          />
        </Filter>
        <Filter label="Service / Expertise">
          <select
            data-testid="expert-report-service"
            onChange={(event) =>
              setFilters({ ...filters, service_id: event.target.value })
            }
            value={filters.service_id}
          >
            <option value="">All services</option>
            {options.services.map((service) => (
              <option key={service.service_id} value={service.service_id}>
                {service.name}
              </option>
            ))}
          </select>
        </Filter>
        <Filter label="Status">
          <select
            data-testid="expert-report-status"
            onChange={(event) =>
              setFilters({ ...filters, status: event.target.value })
            }
            value={filters.status}
          >
            <option value="">All statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </Filter>
        {type === "commercial" && (
          <Filter label="Commercial Mode">
            <select
              data-testid="expert-report-mode"
              onChange={(event) =>
                setFilters({ ...filters, mode: event.target.value })
              }
              value={filters.mode}
            >
              <option value="">All modes</option>
              {options.commercial_modes.map((mode) => (
                <option key={mode} value={mode}>
                  {mode.replaceAll("_", " ")}
                </option>
              ))}
            </select>
          </Filter>
        )}
        <div className="flex items-end">
          <Button
            className="w-full"
            data-testid="expert-report-apply-filters"
            onClick={() => load(1)}
            type="button"
          >
            Apply Filters
          </Button>
        </div>
      </div>

      {type === "commercial" && (
        <p
          className="mt-4 border-l-4 border-slate-400 bg-slate-100 p-3 text-xs text-slate-700"
          data-testid="expert-report-commercial-notice"
        >
          Commercial figures are stored calculation records. They do not
          represent a completed payment or payout.
        </p>
      )}

      <ReportRows rows={result.rows} type={type} />
      <div
        className="mt-5 flex items-center justify-between gap-3 text-sm text-slate-600"
        data-testid="expert-report-pagination"
      >
        <span>
          {result.pagination.total} record
          {result.pagination.total === 1 ? "" : "s"}
        </span>
        <div className="flex gap-2">
          <Button
            data-testid="expert-report-previous"
            disabled={result.pagination.page <= 1}
            onClick={() => load(result.pagination.page - 1)}
            size="sm"
            type="button"
            variant="outline"
          >
            Previous
          </Button>
          <Button
            data-testid="expert-report-next"
            disabled={!result.pagination.has_next}
            onClick={() => load(result.pagination.page + 1)}
            size="sm"
            type="button"
            variant="outline"
          >
            Next
          </Button>
        </div>
      </div>
    </section>
  );
}

function ReportRows({ rows, type }) {
  if (!rows.length)
    return (
      <div
        className="mt-6 border border-dashed border-slate-200 bg-white p-8 text-center"
        data-testid="expert-report-empty"
      >
        <p className="font-semibold text-slate-900">
          No {TYPES[type].toLowerCase()} records
        </p>
        <p className="mt-1 text-sm text-slate-600">
          Adjust the filters or check back after activity is recorded.
        </p>
      </div>
    );
  return (
    <div className="mt-6 space-y-3" data-testid="expert-report-results">
      {rows.map((row) => (
        <article
          className="border border-slate-200 bg-white p-5 shadow-sm"
          data-testid={`expert-report-row-${row.reference}`}
          key={row.reference}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-slate-900">{row.reference}</p>
              <p className="mt-1 text-sm text-slate-600">
                {row.service_name} · {row.society_name}
              </p>
            </div>
            <span className="bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
              {String(row.status || "").replaceAll("_", " ")}
            </span>
          </div>
          {type === "appointments" && (
            <div className="mt-4 grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
              <p>Scheduled: {formatTimestamp(row.date_time)}</p>
              <p>Duration: {row.duration_minutes} minutes</p>
            </div>
          )}
          {type === "consultations" && (
            <div className="mt-4 grid gap-3 text-sm text-slate-600 sm:grid-cols-3">
              <p>Actual start: {formatTimestamp(row.actual_start_utc)}</p>
              <p>Actual end: {formatTimestamp(row.actual_end_utc)}</p>
              <p>Duration: {formatDuration(row.actual_duration_seconds)}</p>
            </div>
          )}
          {type === "commercial" && (
            <div className="mt-4 grid gap-3 text-sm text-slate-600 sm:grid-cols-4">
              <p>Calculated: {formatTimestamp(row.calculated_at)}</p>
              <p>
                Mode: {String(row.commercial_mode || "").replaceAll("_", " ")}
              </p>
              <p>Gross Calculated Value: {formatPaise(row.gross_paise)}</p>
              <p>
                Expert Calculated Entitlement:{" "}
                {formatPaise(row.expert_entitlement_paise)}
              </p>
            </div>
          )}
        </article>
      ))}
    </div>
  );
}

function Filter({ label, children }) {
  return (
    <label className="block text-xs font-semibold text-slate-600">
      {label}
      <span className="mt-1 block [&_input]:h-10 [&_input]:w-full [&_input]:border [&_input]:border-slate-200 [&_input]:px-2 [&_select]:h-10 [&_select]:w-full [&_select]:border [&_select]:border-slate-200 [&_select]:bg-white [&_select]:px-2">
        {children}
      </span>
    </label>
  );
}
